import { Injectable, effect, inject, signal } from '@angular/core';
import { Overlay, Rendering } from '@models';
import { DrawService, SimulationService, TaskService } from '@services';
import { SceneStore } from '../stores/scene.store';

@Injectable({
  providedIn: 'root',
})
export class RenderService {
  private drawService = inject(DrawService);
  private sceneStore = inject(SceneStore);
  private simulationService = inject(SimulationService);
  private taskService = inject(TaskService);

  constructor() {
    this.simulationService.nextCycle.subscribe(() => {
      this.checkStaticTimeouts();
      this.drawService.clearCanvas('dynamic');
      this.drawingLoop(this.dynamicRenderings, 'dynamic');
      this.clearDynamicOverlays();
    });

    this.taskService.skippedTask.subscribe(() => {
      this.clearStaticOverlays();
      this.clearDynamicOverlays();
    });

    effect(
      () => {
        if (this.sceneStore.selectedScene()) {
          this.clearStaticOverlays();
        }
      },
      { allowSignalWrites: true },
    );

    effect(() => {
      const renderings = this.staticRenderings().map((rendering) => ({
        ...rendering,
        timeStamp: null,
      }));
      if (
        renderings.length !== 0 &&
        JSON.stringify(renderings) !==
          JSON.stringify(this.previousStaticRenderings)
      ) {
        this.drawService.clearCanvas('static');
        this.drawingLoop(this.staticRenderings(), 'static');
      }
      if (renderings.length > 0) {
        this.previousStaticRenderings = renderings;
      }
    });

    effect(
      () => {
        if (this.simulationService.state()) {
          this.clearStaticOverlays();
        }
      },
      { allowSignalWrites: true },
    );
  }

  staticRenderings = signal<Rendering[]>([]);
  dynamicRenderings: Rendering[] = [];

  renderTimeouts = new Map<string, number>();

  idHistory: string[] = [];

  previousStaticRenderings: Rendering[] = [];

  drawingLoop(renderings: Rendering[], canvas: 'static' | 'dynamic') {
    renderings = renderings.map((rendering) => ({
      ...rendering,
      timeStamp: null,
    }));
    renderings.forEach((rendering) => {
      switch (rendering.overlay.type) {
        case 'rectangle': {
          this.drawService.drawRectangle(rendering.overlay, canvas);
          break;
        }
        case 'circle': {
          this.drawService.drawCircle(rendering.overlay, canvas);
          break;
        }
        case 'triangle': {
          this.drawService.drawTriangle(rendering.overlay, canvas);
          break;
        }
        case 'image': {
          this.drawService.drawImage(rendering.overlay, canvas);
          break;
        }
        case 'text': {
          this.drawService.drawText(rendering.overlay, canvas);
          break;
        }
      }
    });
  }
  addStatic(rendering: Rendering) {
    if (
      (this.staticRenderings().some(
        (staticRendering) => staticRendering.id === rendering?.replace?.id,
      ) ||
        !this.staticRenderings().some(
          (staticRendering) => staticRendering?.id === rendering.id,
        ) ||
        (rendering.overlay.value !== undefined &&
          rendering.overlay.value !==
            this.staticRenderings().filter(
              (staticRendering) => staticRendering.id === rendering.id,
            )[0].overlay.value)) &&
      !this.idHistory.includes(rendering.id)
    ) {
      if (rendering.replace?.persist) {
        this.idHistory.push(rendering.replace.id);
      }
      const renderings = this.staticRenderings().filter(
        (renderingToCheck) =>
          renderingToCheck.id !== rendering.id &&
          renderingToCheck.id !== rendering?.replace?.id,
      );
      this.staticRenderings.set([
        ...renderings,
        {
          ...rendering,
          timeStamp: Date.now(),
        },
      ]);
    }
  }

  addDynamic(rendering: { overlay: Overlay }) {
    this.dynamicRenderings.push({
      id: 'dynamic',
      overlay: rendering.overlay,
    });
  }

  checkStaticTimeouts() {
    const length = this.staticRenderings().length;
    const idsToRemove = this.staticRenderings()
      .filter(
        (rendering) =>
          rendering.timeout &&
          rendering.timeout &&
          Date.now() - rendering.timeStamp > rendering.timeout,
      )
      .map((idToRemove) => idToRemove.id);
    if (idsToRemove.length > 0) {
      this.drawService.clearCanvas('static');
    }
    this.idHistory.push(...idsToRemove);
    const newRenderings = this.staticRenderings().filter(
      (rendering) =>
        !idsToRemove.some((idToRemove) => idToRemove === rendering.id),
    );
    if (length !== newRenderings.length) {
      this.staticRenderings.set(newRenderings);
    }
  }

  clearStaticOverlays() {
    this.staticRenderings.set([]);
    this.drawService.clearCanvas('static');
  }

  clearDynamicOverlays() {
    this.dynamicRenderings = [];
  }

  isInHistory(id: string) {
    return this.idHistory.includes(id);
  }

  async waitForStaticCleanup() {
    return new Promise((resolve) => {
      const interval = setInterval(() => {
        if (this.staticRenderings().length === 0) {
          clearInterval(interval);
          resolve(true);
        }
      }, 100);
    });
  }

  waitForIdTimeout(id: string, seconds: number) {
    if (!this.renderTimeouts.has(id)) {
      this.renderTimeouts.set(id, Date.now());
    }
    return Date.now() - this.renderTimeouts.get(id) > seconds * 1000;
  }
}
